import type { inferOutput } from "@trpc/tanstack-react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import { Button } from "@quake/shared-ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@quake/shared-ui/components/ui/radio-group";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { Textarea } from "@quake/shared-ui/components/ui/textarea";
import { toast } from "@quake/shared-ui/lib/sonner";
import { cn } from "@quake/shared-ui/lib/utils";
import {
  DEFAULT_LIST_EXPIRY_DAYS,
  type ListMode,
  type ListRecordType,
  type ListSharingMode,
} from "@quake/isomorphic/lists";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { trpc } from "~/utils/trpc";
import { UserPicker, type UserOption } from "~/components/user-picker";

export type ListType = inferOutput<typeof trpc.lists.listTypes>[number];
export type ListSummary = inferOutput<typeof trpc.lists.list>[number];
export type ListDetails = inferOutput<typeof trpc.lists.getById>;

export const entityLabels: Record<ListRecordType, string> = {
  candidate: "Candidates",
  job: "Jobs",
  client: "Clients",
  contact: "Contacts",
};

export const visibilityLabels: Record<ListSharingMode, string> = {
  private: "Only me",
  users: "Shared with people",
  dataset: "Shared with dataset",
};

export function formatDate(value: Date | string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

export function formatDateTime(value: Date | string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

export function formatExpiryDate(value: Date | string | null | undefined) {
  return value ? formatDate(value) : "Never";
}

export function isListExpired(list: Pick<ListSummary | ListDetails, "expires_at">) {
  return list.expires_at !== null && new Date(list.expires_at).getTime() <= Date.now();
}

export function isListLocked(list: Pick<ListSummary | ListDetails, "expires_at" | "status">) {
  return list.status === "archived" || isListExpired(list);
}

export function isListOwner(
  list: Pick<ListSummary | ListDetails, "owner_user_dataset_id">,
  userDatasetId: string | null | undefined,
) {
  return !!userDatasetId && list.owner_user_dataset_id === userDatasetId;
}

export function canManageList(
  list: Pick<ListSummary | ListDetails, "owner_user_dataset_id">,
  userDatasetId: string | null | undefined,
  isAdmin: boolean,
) {
  return isAdmin || isListOwner(list, userDatasetId);
}

export function toDateInputValue(value: Date | string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateInputToDate(value: string) {
  if (!value) return undefined;
  return new Date(`${value}T23:59:59.000`);
}

function getDefaultExpiryDate() {
  const date = new Date();
  date.setDate(date.getDate() + DEFAULT_LIST_EXPIRY_DAYS);
  return date;
}

export function ListStateBadge({ list }: { list: Pick<ListSummary | ListDetails, "status" | "expires_at"> }) {
  if (list.status === "archived") {
    return <Badge variant="secondary">Archived</Badge>;
  }
  if (isListExpired(list)) {
    return <Badge variant="destructive">Expired</Badge>;
  }
  return (
    <Badge className="bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300">Active</Badge>
  );
}

export function RecordTypeBadge({ entityType }: { entityType: ListRecordType }) {
  return <Badge variant="outline">{entityLabels[entityType]}</Badge>;
}

export function VisibilityBadge({ visibility }: { visibility: ListSharingMode }) {
  return <Badge variant="secondary">{visibilityLabels[visibility]}</Badge>;
}

export type ListFormPayload = {
  name: string;
  listTypeId: string;
  description: string | null;
  context: string | null;
  mode: ListMode;
  remoteId: string | null;
  expiresAt?: Date | null;
  sharing?: { mode: ListSharingMode; userDatasetIds: string[] };
};

export function ListFormDialog({
  open,
  onOpenChange,
  listTypes,
  initialList,
  isSubmitting,
  canManageSharing = true,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listTypes: ListType[];
  initialList?: ListSummary | ListDetails | null;
  isSubmitting?: boolean;
  canManageSharing?: boolean;
  onSubmit: (payload: ListFormPayload) => Promise<void>;
}) {
  const activeListTypes = useMemo(() => listTypes.filter((listType) => listType.status === "active"), [listTypes]);
  const defaultListType = activeListTypes[0];
  const initialListType = useMemo(() => {
    return listTypes.find((listType) => listType.id === initialList?.list_type_id) ?? defaultListType;
  }, [listTypes, initialList?.list_type_id, defaultListType]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [context, setContext] = useState("");
  const [listTypeId, setListTypeId] = useState("");
  const [remoteId, setRemoteId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [neverExpires, setNeverExpires] = useState(false);
  const [expiryChanged, setExpiryChanged] = useState(false);
  const [sharingMode, setSharingMode] = useState<ListSharingMode>("private");
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const isSharingEditable = !initialList || canManageSharing;
  const sharingQuery = useQuery({
    ...trpc.lists.getSharing.queryOptions({ id: initialList?.id ?? "" }),
    enabled: open && !!initialList && isSharingEditable,
  });

  useEffect(() => {
    if (!open) return;
    setName(initialList?.name ?? "");
    setDescription(initialList?.description ?? "");
    setContext(initialList?.context ?? "");
    setListTypeId(initialListType?.id ?? "");
    setRemoteId(initialList?.remote_id ?? "");
    setNeverExpires(initialList?.expires_at === null);
    setExpiresAt(toDateInputValue(initialList?.expires_at ?? getDefaultExpiryDate()));
    setExpiryChanged(false);
    setSharingMode(initialList?.sharing_mode ?? (initialList?.visibility === "dataset_shared" ? "dataset" : "private"));
    setSelectedUsers([]);
  }, [open, initialList, initialListType]);

  useEffect(() => {
    if (!open || !sharingQuery.data) return;
    setSharingMode(sharingQuery.data.mode);
    setSelectedUsers(sharingQuery.data.userDatasetIds.map((id) => ({ id })));
  }, [open, sharingQuery.data]);

  const selectedListType = useMemo(() => {
    return listTypes.find((listType) => listType.id === listTypeId);
  }, [listTypes, listTypeId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!listTypeId) {
      toast.error("Select a list type.");
      return;
    }
    if (isSharingEditable && sharingMode === "users" && selectedUsers.length === 0) {
      toast.error("Select at least one person.");
      return;
    }
    await onSubmit({
      name: name.trim(),
      listTypeId,
      description: description.trim() || null,
      context: context.trim() || null,
      mode: "static",
      remoteId: remoteId || null,
      expiresAt: initialList && !expiryChanged ? undefined : neverExpires ? null : dateInputToDate(expiresAt),
      ...(isSharingEditable
        ? {
            sharing: {
              mode: sharingMode,
              userDatasetIds: sharingMode === "users" ? selectedUsers.map((user) => user.id) : [],
            },
          }
        : {}),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initialList ? "Edit list" : "Create list"}</DialogTitle>
          <DialogDescription>Set the working scope, visibility, and expiry for this list.</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="list-name">Name</Label>
            <Input id="list-name" value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="list-description">Description</Label>
            <Textarea
              id="list-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional description for this list"
              className="min-h-20"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="list-context">Context</Label>
            <Textarea
              id="list-context"
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder="Optional context for this list"
              className="min-h-20"
            />
          </div>
          <div className="grid items-start gap-4">
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={listTypeId} onValueChange={setListTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {listTypes
                    .filter((listType) => listType.status === "active" || listType.id === initialList?.list_type_id)
                    .map((listType) => (
                      <SelectItem key={listType.id} value={listType.id}>
                        {listType.name}
                        {listType.status === "archived" && " (Archived)"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid items-start gap-4">
            <div className="grid gap-2">
              <Label htmlFor="list-expiry">Expires</Label>
              <Input
                id="list-expiry"
                type="date"
                value={expiresAt}
                onChange={(event) => {
                  setExpiresAt(event.target.value);
                  setExpiryChanged(true);
                }}
                disabled={neverExpires}
              />
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={neverExpires}
                  onCheckedChange={(checked) => {
                    setNeverExpires(checked);
                    setExpiryChanged(true);
                  }}
                  aria-label="Never expires"
                />
                Never expires
              </div>
              {!initialList && selectedListType ? (
                <p className="text-xs text-muted-foreground">Default: one year</p>
              ) : null}
            </div>
            {isSharingEditable ? (
              <div className="grid gap-2">
                <Label>Visibility</Label>
                <RadioGroup
                  value={sharingMode}
                  onValueChange={(value) => setSharingMode(value as ListSharingMode)}
                  className="gap-3 rounded-md border p-3"
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="private" id="list-share-private" className="mt-0.5" />
                    <Label htmlFor="list-share-private" className="cursor-pointer font-normal">
                      <span className="font-medium">Only me</span>
                      <p className="mt-0.5 text-xs text-muted-foreground">Only you can access this list.</p>
                    </Label>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="users" id="list-share-users" className="mt-0.5" />
                      <Label htmlFor="list-share-users" className="cursor-pointer font-normal">
                        <span className="font-medium">Share with specific people</span>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Only selected workspace members can access this list.
                        </p>
                      </Label>
                    </div>
                    {sharingMode === "users" ? (
                      <div className="pl-7">
                        <UserPicker value={selectedUsers} onValueChange={setSelectedUsers} placeholder="Add people…" />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="dataset" id="list-share-dataset" className="mt-0.5" />
                    <Label htmlFor="list-share-dataset" className="cursor-pointer font-normal">
                      <span className="font-medium">Share with dataset</span>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Everyone in this dataset can access this list.
                      </p>
                    </Label>
                  </div>
                </RadioGroup>
                {sharingQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading current sharing…</p>
                ) : null}
                {sharingQuery.isError ? (
                  <p className="text-xs text-muted-foreground">Could not load sharing settings.</p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="list-remote-id">Remote ID</Label>
              <Input
                id="list-remote-id"
                value={remoteId}
                onChange={(event) => setRemoteId(event.target.value)}
                placeholder="vendor-record-id"
              />
              <p className="text-xs text-muted-foreground">Optional caller-defined ID. No source is required.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || activeListTypes.length === 0 || (!!initialList && sharingQuery.isLoading)}
            >
              {isSubmitting ? "Saving..." : initialList ? "Save changes" : "Create list"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmListActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  isSubmitting,
  destructive,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  isSubmitting?: boolean;
  destructive?: boolean;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            disabled={isSubmitting}
            onClick={async () => {
              await onConfirm();
              onOpenChange(false);
            }}
          >
            {isSubmitting ? "Working..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RenewListDialog({
  open,
  onOpenChange,
  list,
  onRenew,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list?: Pick<ListSummary | ListDetails, "name" | "expires_at"> | null;
  onRenew: (expiresAt: Date) => Promise<void>;
  isSubmitting?: boolean;
}) {
  const [expiresAt, setExpiresAt] = useState("");

  useEffect(() => {
    if (!open) return;
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + DEFAULT_LIST_EXPIRY_DAYS);
    setExpiresAt(toDateInputValue(defaultDate));
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextDate = dateInputToDate(expiresAt);
    if (!nextDate) {
      toast.error("Choose a renewal date.");
      return;
    }
    await onRenew(nextDate);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renew list</DialogTitle>
          <DialogDescription>
            {list?.name ? `Set a new expiry for ${list.name}.` : "Set a new expiry date."}
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="renew-list-expiry">Expires</Label>
            <Input
              id="renew-list-expiry"
              type="date"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Renewing..." : "Renew"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ListTypesDialog({
  open,
  onOpenChange,
  listTypes,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listTypes: ListType[];
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const invalidateListTypes = async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.lists.listTypes.queryFilter()),
      queryClient.invalidateQueries(trpc.lists.list.queryFilter()),
    ]);
  };

  const createListType = useMutation(
    trpc.lists.createListType.mutationOptions({
      onSuccess: async () => {
        setName("");
        setDescription("");
        await invalidateListTypes();
        toast.success("List type created.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const updateListType = useMutation(
    trpc.lists.updateListType.mutationOptions({
      onSuccess: async () => {
        await invalidateListTypes();
        toast.success("List type updated.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Type name is required.");
      return;
    }
    await createListType.mutateAsync({
      name: name.trim(),
      description: description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>List types</DialogTitle>
          <DialogDescription>
            Reusable categories for shortlists, pools, campaigns, and other list workflows.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          <form className="grid gap-3 rounded-md border bg-muted/20 p-3" onSubmit={handleCreate}>
            <div className="grid gap-3 sm:grid-cols-[1fr_1.3fr_auto] sm:items-end">
              <div className="grid gap-1.5">
                <Label htmlFor="list-type-name">Name</Label>
                <Input id="list-type-name" value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="list-type-description">Description</Label>
                <Input
                  id="list-type-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
              <Button type="submit" disabled={createListType.isPending}>
                Add type
              </Button>
            </div>
          </form>
          <div className="max-h-80 overflow-auto rounded-md border">
            {listTypes.map((listType) => (
              <div
                key={listType.id}
                className="grid gap-3 border-b p-3 last:border-b-0 sm:grid-cols-[1fr_1.3fr_7rem] sm:items-center"
              >
                <div>
                  <div className="text-sm font-medium">{listType.name}</div>
                  <div className="text-xs text-muted-foreground">Created {formatDate(listType.created_at)}</div>
                </div>
                <div className="text-sm text-muted-foreground">{listType.description || "-"}</div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(listType.status === "archived" && "text-muted-foreground")}
                  disabled={updateListType.isPending}
                  onClick={() => {
                    updateListType.mutate({
                      id: listType.id,
                      status: listType.status === "active" ? "archived" : "active",
                    });
                  }}
                >
                  {listType.status === "active" ? "Archive" : "Restore"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
